import React, { useMemo, useState } from "react";
import { StepUpSpinner } from "../../../Components/Loader";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  Box,
  Button,
  Card,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import {
  useGetPaymentQuery,
  useGetallStudentQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useNavigate } from "react-router-dom";
import useUser from "../../../Core/hook/useUser";
import { useStyles } from "../../../theme/appStyles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Pagination from "../../../Components/Pagination";
import useIsMobile from "../../../Core/hook/useIsMobile";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { canCreate } = useUser();
  const { showErrorMessage } = useNotifier();
  const isMobileRes = useIsMobile();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [studentFilter, setStudentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const { data, isLoading, isError } = useGetPaymentQuery({
    studentId: studentFilter,
    status: statusFilter,
    startDate: startDate ? startDate.format("YYYY-MM-DD") : "",
    endDate: endDate ? endDate.format("YYYY-MM-DD") : "",
  });

  const { data: studentData } = useGetallStudentQuery({ searchText: "", id: "" });
  const studentList = useMemo(() => Array.isArray(studentData?.data) ? (studentData?.data as any[]) : [], [studentData]);

  const payments: any[] = useMemo(() => Array.isArray(data?.data) ? (data?.data as any[]) : [], [data]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "completed": return "success";
      case "pending": return "warning";
      case "failed": return "error";
      case "refunded": return "info";
      default: return "default";
    }
  };

  return (
    <Card className={classes.card}>
      <Grid
        container
        justifyContent={"space-between"}
        spacing={2}
        sx={{ padding: 2 }}
        className={classes.titleGrid}
      >
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Typography className={classes.cardTitle}>Payments</Typography>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ padding: 2 }}>
        <Grid item lg={2} md={3} sm={6} xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel>Student</InputLabel>
            <Select value={studentFilter} label="Student" onChange={(e) => setStudentFilter(e.target.value)}>
              <MenuItem value="">All Students</MenuItem>
              {studentList.map((s: any) => (
                <MenuItem key={s.id} value={s.id}>{s.firstName} {s.lastName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item lg={2} md={3} sm={6} xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="refunded">Refunded</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid item lg={2} md={3} sm={6} xs={12}>
            <DatePicker
              label="From"
              value={startDate}
              onChange={(val) => setStartDate(val)}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </Grid>
          <Grid item lg={2} md={3} sm={6} xs={12}>
            <DatePicker
              label="To"
              value={endDate}
              onChange={(val) => setEndDate(val)}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </Grid>
        </LocalizationProvider>

        <Grid item lg={4} md={12} sm={12} xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
          {canCreate("Payment") && (
          <Button className={classes.addNewButton} onClick={() => navigate("/payment/create")}>
            Record Payment
          </Button>
          )}
        </Grid>
      </Grid>

      <Grid>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead className={classes.tableHeaderRow}>
              <TableRow>
                <TableCell className={classes.tablheader}><strong>Date</strong></TableCell>
                <TableCell className={classes.tablheader}><strong>Student</strong></TableCell>
                <TableCell className={classes.tablheader} sx={{ textAlign: "right" }}><strong>Amount</strong></TableCell>
                {!isMobileRes && (
                  <>
                    <TableCell className={classes.tablheader}><strong>Type</strong></TableCell>
                    <TableCell className={classes.tablheader}><strong>Invoice #</strong></TableCell>
                  </>
                )}
                <TableCell className={classes.tablheader}><strong>Status</strong></TableCell>
                <TableCell className={classes.tablheader} sx={{ textAlign: "right" }}><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    <Box display="flex" justifyContent="center" alignItems="center" height="10vh">
                      <StepUpSpinner size="medium" />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : isError || payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    {isError ? "Error fetching data" : "No payments found"}
                  </TableCell>
                </TableRow>
              ) : (
                payments
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: any) => (
                    <TableRow key={row.id} className={classes.tableRowHover}>
                      <TableCell className={classes.tablCell}>
                        {row.paymentDate ? dayjs(row.paymentDate).format("YYYY-MM-DD") : ""}
                      </TableCell>
                      <TableCell className={classes.tablCell}>
                        {row.studentName || `${row.studentFirstName || ""} ${row.studentLastName || ""}`}
                      </TableCell>
                      <TableCell className={classes.tablCell} sx={{ textAlign: "right" }}>
                        ${parseFloat(row.amount || 0).toFixed(2)}
                      </TableCell>
                      {!isMobileRes && (
                        <>
                          <TableCell className={classes.tablCell}>{row.paymentType || row.type || ""}</TableCell>
                          <TableCell className={classes.tablCell}>{row.invoiceNumber || row.invoiceId || ""}</TableCell>
                        </>
                      )}
                      <TableCell className={classes.tablCell}>
                        <Chip label={row.status || "Completed"} color={getStatusColor(row.status) as any} size="small" />
                      </TableCell>
                      <TableCell className={classes.tablCell}>
                        <Grid container spacing={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                          <Grid item>
                            <Button
                              className={classes.editButtonList}
                              onClick={() => navigate(`/payment/view/${row.id}`)}
                            >
                              <VisibilityIcon className={classes.editListIcon} />
                            </Button>
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
          <Grid spacing={5} p={"18px"} py={4} justifyContent={"end"} container textAlign={"center"} alignContent={"center"} alignItems={"center"}>
            <Grid sx={{ textAlign: "end", justifyContent: "flex-end", alignItems: "flex-end" }}>
              <Pagination count={payments.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} />
            </Grid>
          </Grid>
        </TableContainer>
      </Grid>
    </Card>
  );
};

export default Index;
